import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import { Button } from '@src/common/components/button/Button';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

export const Dropzone = ({
  isSmall,
  getRootProps,
  getInputProps,
  open,
  singleFile,
  fileExtensionsMessage,
  additionalAction,
}: {
  isSmall: boolean;
  getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  open: () => void;
  singleFile?: boolean;
  fileExtensionsMessage?: string;
  additionalAction?: JSX.Element;
}) => {
  const { t } = useTranslation();

  return (
    <div
      {...getRootProps({ className: 'dropzone' })}
      className={isSmall ? smallDropzone : dropzone}
    >
      <input
        data-testid="dropzone"
        {...getInputProps({ multiple: !singleFile })}
      />
      <Stack
        direction={isSmall ? 'row' : 'column'}
        alignItems="center"
        justifyContent={isSmall ? 'space-between' : 'flex-start'}
        gap={theme.spacing['3']}
      >
        <Stack
          direction={isSmall ? 'row' : 'column'}
          alignItems="center"
          gap={theme.spacing['4']}
        >
          <FileUploadOutlined fontSize="large" />
          <Stack alignItems={isSmall ? 'start' : 'center'}>
            <Typography variant="body1">{t('dragFile')}</Typography>
            {fileExtensionsMessage && (
              <Typography variant="body2">{fileExtensionsMessage}</Typography>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" gap={theme.spacing['4']}>
          <Button variant="contained" onClick={open}>
            {singleFile ? t('selectFile') : t('selectFiles')}
          </Button>

          {additionalAction && additionalAction}
        </Stack>
      </Stack>
    </div>
  );
};

const dropzone = css(() => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'rgba(219, 225, 255, 0.08)',
  height: '256px',
  border: '2px dashed #757680',
  borderRadius: '8px',
  justifyContent: 'center',
}));

const smallDropzone = css(() => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'rgba(219, 225, 255, 0.08)',
  padding: '32px 28px',
  height: '88px',
  border: '2px dashed #757680',
  borderRadius: '8px',
  justifyContent: 'center',
}));
